// src/screens/PaymentScreen.js
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { savePaymentMethod } from '../redux/features/Cart/cartSlice';
import CheckoutSteps from '../components/CheckoutSteps';
import { CreditCard, Wallet, CheckCircle, ChevronLeft, Lock, ShieldCheck, Sparkles } from 'lucide-react';

const COLORS = {
  primary: '#8A9A7B',
  primaryLight: '#9EAD8C',
  primaryDark: '#6A7A5B',
  primaryBg: '#F0F4EB',
  border: '#DCE4D3',
  danger: '#D4837A',
  success: '#7A9B8A',
};

const paymentOptions = [
  { 
    id: 'zibal', 
    label: 'زیبال', 
    icon: CreditCard, 
    description: 'پرداخت امن از طریق درگاه زیبال', 
    badge: 'پیشنهادی', 
    badgeColor: 'bg-[#8A9A7B]' 
  },
  { 
    id: 'snapppay',
    label: 'اسنپ پی',
    icon: Wallet, 
    description: 'پرداخت سریع از طریق اسنپ پی',
    badge: null 
  },
];

const PaymentOptionCard = ({ option, isSelected, onSelect }) => {
  const Icon = option.icon;
  return (
    <div 
      onClick={() => onSelect(option.id)} 
      className={`relative rounded-2xl border-2 p-4 cursor-pointer transition-all duration-300 ${
        isSelected 
          ? 'border-[#8A9A7B] bg-[#F0F4EB] shadow-lg shadow-[#8A9A7B]/20' 
          : 'border-gray-200 bg-white hover:border-[#9EAD8C] hover:shadow-md'
      }`}
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 mt-1">
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
            isSelected 
              ? 'border-[#8A9A7B] bg-[#8A9A7B]' 
              : 'border-gray-300 bg-white'
          }`}>
            {isSelected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <div className={`p-2.5 rounded-xl transition-all ${
              isSelected 
                ? 'bg-[#8A9A7B] text-white shadow-md' 
                : 'bg-gray-100 text-gray-500'
            }`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className={`font-bold text-base ${
                  isSelected ? 'text-[#3D4A33]' : 'text-gray-700'
                }`}>
                  {option.label}
                </p>
                {option.badge && (
                  <span className={`text-[10px] font-bold text-white px-2.5 py-0.5 rounded-full ${option.badgeColor}`}>
                    {option.badge}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-0.5">{option.description}</p>
            </div>
          </div>
        </div>

        {isSelected && (
          <div className="flex-shrink-0">
            <span className="inline-flex items-center gap-1 text-xs font-medium bg-[#7A9B8A] text-white px-2.5 py-0.5 rounded-full">
              <CheckCircle className="w-3 h-3" />
              انتخاب شده
            </span>
          </div>
        )}
      </div>

      {isSelected && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#8A9A7B] to-[#9EAD8C] rounded-b-2xl" />
      )}
    </div>
  );
};

function PaymentScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ✅ تغییر از userLogin به auth
  const { userInfo } = useSelector((state) => state.auth) || {};
  const { shippingAddress, paymentMethod } = useSelector((state) => state.cart);
  const [selectedMethod, setSelectedMethod] = useState(paymentMethod || '');

  useEffect(() => {
    if (!userInfo?.access) {
      navigate('/phone?redirect=/payment');
      return;
    }
    if (!shippingAddress?.address) {
      navigate('/shipping');
    }
  }, [shippingAddress, navigate, userInfo]);

  const submitHandler = (e) => {
    e.preventDefault();
    if (selectedMethod) {
      dispatch(savePaymentMethod(selectedMethod));
      navigate('/placeorder');
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F4EB] py-8 px-4" dir="rtl">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6"><CheckoutSteps step1 step2 step3 /></div>
        <div className="mt-6"><h1 className="text-2xl md:text-3xl font-bold text-[#3D4A33] flex items-center gap-3"><CreditCard className="w-8 h-8 text-[#8A9A7B]" /> روش پرداخت</h1><p className="text-gray-500 text-sm mt-1">روش پرداخت مورد نظر خود را انتخاب کنید.</p></div>
        <div className="mt-6 bg-white rounded-2xl shadow-xl p-4 md:p-6">
          <form onSubmit={submitHandler}>
            <div className="space-y-4">{paymentOptions.map((option) => (<PaymentOptionCard key={option.id} option={option} isSelected={selectedMethod === option.id} onSelect={setSelectedMethod} />))}</div>
            <div className="mt-6">
              <button type="submit" disabled={!selectedMethod} className={`w-full py-4 rounded-2xl font-bold text-white text-lg transition-all duration-300 flex items-center justify-center gap-2 ${selectedMethod ? 'bg-[#8A9A7B] hover:bg-[#6A7A5B] shadow-lg hover:shadow-xl active:scale-[0.98]' : 'bg-gray-300 cursor-not-allowed'}`}>
                ادامه به ثبت سفارش <ChevronLeft className="w-5 h-5" />
              </button>
              {!selectedMethod && <p className="text-center text-sm text-gray-400 mt-2">لطفاً یک روش پرداخت را انتخاب کنید</p>}
            </div>
            <div className="mt-6 pt-4 border-t border-gray-100 flex flex-wrap items-center justify-center gap-4 text-xs text-gray-400">
              <div className="flex items-center gap-1.5"><Lock className="w-4 h-4 text-[#8A9A7B]" /><span>اطلاعات شما با بالاترین سطح امنیت محافظت می‌شود</span></div>
              <div className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#8A9A7B]" /><span>درگاه‌های پرداخت معتبر</span></div>
              <div className="flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-[#8A9A7B]" /><span>پرداخت سریع و آسان</span></div>
            </div>
          </form>
        </div>
        <div className="mt-4 text-center text-xs text-gray-400"><p>پس از انتخاب روش پرداخت، به صفحه ثبت سفارش هدایت می‌شوید.</p></div>
      </div>
    </div>
  );
}

export default PaymentScreen;